import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import OptionFilterSelect from '../select'
import GetCompateToInput from './components/getCompareToInput'
import { FilterInputsTypesEnum, FilterOptionsTypesEnum } from 'enum/filterType.enum'
import { getFilterOperators, getFilterOptionsOperators, TFiltersOperator } from 'helpers/operators'
import { IFilterOption } from 'interfaces/filterProps'
import { es_dateFormat } from 'constants/date'
import styles from './index.module.scss'

interface IFilterRow {
  input?: string
  selectedData?: any
  operator?: string
  compareTo?: string
  condition?: string
  closeable?: boolean
  inputOptions: IFilterOption[]
  onChange: any
  translations: any
  onDelete?: any
  showGroupCondition?: boolean
  dateFormat?: string
  disabled?: boolean
  styleGroup?: boolean
}

const InputInitialValue = { data: { value: undefined, label: '' }, type: undefined }

const FilterRow: React.FC<IFilterRow> = ({
  input = '',
  selectedData = undefined,
  inputOptions = [],
  operator = undefined,
  compareTo = undefined,
  condition = 'and',
  onChange,
  translations,
  closeable = true,
  onDelete = undefined,
  showGroupCondition = false,
  dateFormat = es_dateFormat,
  disabled = false,
  styleGroup = false,
}) => {
  const [inputData, setInputData] = useState(InputInitialValue as any)
  const [operatorData, setOperatorData] = useState(InputInitialValue as any)
  const [compareToData, setCompareToData] = useState(InputInitialValue as any)
  const [inputOptionsArray, setInputOptionsArray] = useState([] as IFilterOption[])
  const [operatorType, setOperatorType] = useState([] as any)
  const [compareToList, setCompareToList] = useState(undefined as any)
  const { list, tagsList, hybrid, group } = FilterOptionsTypesEnum
  const conditions = [
    { label: translations.groupConditions.and, value: 'and' },
    { label: translations.groupConditions.or, value: 'or' },
  ]
  const [conditionData, setConditionData] = useState(conditions[0] as any)

  useEffect(() => {
    const getInputData = inputOptions.find(({ value }) => value === input)
    if (getInputData) {
      setInputData({
        data: { label: getInputData.label, value: input, type: getInputData.type, min: getInputData.min, max: getInputData.max },
        type: getInputData.type,
        onSearch: getInputData.onSearch,
        async: getInputData.async,
        addionalFields: getInputData.additionalFields,
      })
      const customFilters = getFilterOptionsOperators(translations.operations, getInputData.filterOptions, getInputData.type)
      const columnsFilters = getFilterOperators(translations.operations, getInputData.type)
      const operators = customFilters || columnsFilters
      setOperatorType(operators)

      if (getInputData.type === list || getInputData.type === tagsList || getInputData.type === hybrid || getInputData.type === group) {
        setCompareToList(inputOptions.find(({ value }) => value === input))
      }
    } else {
      setInputData(InputInitialValue)
    }
  }, [input, inputOptions])

  useEffect(() => {
    if (!!operatorType.length && operator) {
      const op = operatorType.find(({ value }: any) => value === operator)
      setOperatorData({ data: op, type: inputData.type })
    } else {
      setOperatorData(InputInitialValue)
    }
  }, [operator, operatorType])

  useEffect(() => {
    let compareToValue = { value: compareTo }
    if (compareToList && (inputData.type === list || inputData.type === tagsList || inputData.type === hybrid || inputData.type === group)) {
      const exist = compareToList?.list?.find(({ value }: IFilterOption) => value === compareTo)
      if (exist) compareToValue = exist
    }
    setCompareToData(compareToValue ? { data: compareToValue } : InputInitialValue)
  }, [compareTo, compareToList])

  useEffect(() => {
    setInputOptionsArray(inputOptions)
  }, [inputOptions, inputData, compareToList])

  useEffect(() => {
    if (conditionData.value !== condition) {
      const aux = conditions.find(({ value }) => value === condition)
      setConditionData(aux)
    }
  }, [condition])

  const handleChange = (selectedValue: IFilterOption = InputInitialValue, input: string) => {
    onChange({
      input,
      async: selectedValue.async,
      additionalFields: selectedValue.additionalFields,
      type: input === FilterInputsTypesEnum.input ? selectedValue.type : inputData.type,
      values: Array.isArray(selectedValue) ? selectedValue.pop() : selectedValue,
    })
  }

  const getOperator = () => {
    const columnData = inputOptions.find(({ value }) => value === input)
    if (columnData) {
      if (columnData.filterOptions) return getFilterOptionsOperators(translations.operations, columnData.filterOptions, inputData.type) as TFiltersOperator[]
      if (columnData.excludedOperators) return getFilterOperators(translations.operations, inputData.type).filter(({ value }) => !columnData.excludedOperators?.includes(value))
    }
    return getFilterOperators(translations.operations, inputData.type)
  }

  return (
    <div className={`${styles.filterRowContainer} ${styleGroup ? styles.styleGroup : ''} filters-row-container`}>
      <OptionFilterSelect
        className={`${styles.input} filters-row-input`}
        dataTest="rankmiTable-filters-input-property"
        disabled={disabled}
        options={inputOptionsArray}
        selectedOption={inputData.data}
        translations={translations}
        onChange={(selectedValue: IFilterOption) => handleChange(selectedValue, FilterInputsTypesEnum.input)}
      />
      <OptionFilterSelect
        className={`${styles.input} filters-row-input`}
        dataTest="rankmiTable-filters-input-operator"
        disabled={!inputData.data.value}
        options={getOperator()}
        selectedOption={operatorData.data}
        translations={translations}
        onChange={(selectedValue: IFilterOption) => handleChange(selectedValue, FilterInputsTypesEnum.operator)}
      />
      <GetCompateToInput
        className={`${styles.input} filters-row-input rankmiTable-filters-input-compare-to`}
        compareToData={compareToData.data}
        dataTest="rankmiTable-filters-input-compare-to"
        dateFormat={dateFormat}
        inputData={inputData}
        inputOptions={inputOptionsArray}
        operatorData={operatorData.data}
        selectedData={selectedData}
        translations={translations}
        onChange={(selectedValue: IFilterOption) => handleChange(selectedValue, FilterInputsTypesEnum.compareTo)}
      />
      {showGroupCondition && (
        <OptionFilterSelect
          className={`${styles.input} ${styles.conditionInput} filters-row-input`}
          dataTest="rankmiTable-filters-input-group-condition"
          disabled={!compareToData || !compareToData.data}
          options={conditions}
          selectedOption={conditionData}
          translations={translations}
          onChange={(selectedValue: IFilterOption) => handleChange(selectedValue, FilterInputsTypesEnum.condition)}
        />
      )}
      {closeable && (
        <span className={styles.close} id="closeFilter" onClick={onDelete}>
          <FontAwesomeIcon icon={faTimes} />
        </span>
      )}
    </div>
  )
}

export default FilterRow
